import React, { useEffect, useState } from "react";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import Button from "@components/buttons/Button/Button";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { deleteGuestBookEntry, fetchMemorial, updateMemorialInfo } from "@services/memorialService";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchBar from "@components/inputs/SearchBar/SearchBar";
import { filterByNameOrMessage } from "@util/filter";
import DeleteConfirmationPopup from "@components/modals/DeleteConfirmationPopup/DeleteConfirmationPopup";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { Memorial } from "@models/Memorial";

const RESULTS_PER_PAGE = 10;

const GuestBookModerationPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [memorial, setMemorial] = useState<Memorial>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [selectedEntryId, setSelectedEntryId] = useState("");

    // Fetch services on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) {
                    throw new Error("No ID.");
                }
                const data = await fetchMemorial(id);
                setMemorial(data);
            } catch (err) {
                setError("Failed to fetch services.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [id]);

    // Filter and paginate posts
    const filteredData = filterByNameOrMessage(memorial?.guestBook || [], searchTerm);
    const totalPages = Math.ceil((filteredData?.length || 0) / RESULTS_PER_PAGE);
    const paginatedData = filteredData?.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    const handleDelete = async () => {
        try {
            setLoading(true);
            if (!id) {
                throw new Error("No memorial ID.");
            }
            if (!selectedEntryId) {
                throw new Error("No entry ID selected.");
            }
            const updatedGuestBook = await deleteGuestBookEntry(id, selectedEntryId);
            setMemorial((prevState) => {
                if (!prevState) {
                    return prevState;
                }
                const newState = { ...prevState };
                newState.guestBook = updatedGuestBook;
                return newState;
            });
        } catch {
            const msg = "Failed to delete entry.";
            setError(msg);
            alert(msg);
        } finally {
            setLoading(false);
        }
        setShowDeletePopup(false);
    };

    const handleOpenCloseGuestBook = async () => {
        try {
            setLoading(true);
            if (!id) {
                throw new Error("No memorial ID.");
            }
            if (!memorial) {
                throw new Error("No memorial.");
            }

            const updatedMemorial = await updateMemorialInfo(id, {
                privacy: {
                    ...memorial.privacy,
                    guestBookOpen: !memorial.privacy.guestBookOpen,
                },
            });
            setMemorial(updatedMemorial);
        } catch {
            const msg = "Failed to update guest book status.";
            setError(msg);
            alert(msg);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (paginatedData?.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>{`No entries ${searchTerm ? "found" : "yet"}.`}</div>;
        }

        return (
            <div className={commonStyles.itemList}>
                {paginatedData?.map((data) => (
                    <InfoCard
                        key={data.id}
                        title={`${data.name}`}
                        items={[
                            data.message || "",
                        ]}
                        buttons={[
                            {
                                title: "Delete",
                                variant: "cancel",
                                onClick: () => {
                                    setShowDeletePopup(true);
                                    setSelectedEntryId(data.id);
                                },
                            },
                        ]}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            {!loading && !error && (
                <div className={commonStyles.headerContainer}>
                    <h1>{`${memorial?.firstName} ${memorial?.lastName} - Guest Book`}</h1>
                    <div className={commonStyles.buttonGroup}>
                        <Button variant="primary" size="medium" onClick={handleOpenCloseGuestBook}>
                            {`${memorial?.privacy.guestBookOpen ? "Close" : "Open"} Guest Book`}
                        </Button>
                        <Button variant="submit" size="medium" onClick={() => navigate(-1)}>
                            Finish
                        </Button>
                    </div>
                </div>
            )}
            {!loading && (
                <div className={commonStyles.searchBar}>
                    <SearchBar value={searchTerm} onChange={(val) => setSearchTerm(val)} />
                </div>
            )}
            {renderContent()}
            {totalPages > 1 && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}
            <DeleteConfirmationPopup
                visible={showDeletePopup}
                onConfirm={handleDelete}
                onCancel={() => setShowDeletePopup(false)}
            />
        </div>
    );
};

export default GuestBookModerationPage;
